/**
 * 将 REITs 数据转换为 el-cascader 的选项数组
 * @param {Array} data - 原始数据数组
 * @returns {Array} el-cascader 选项数组
 */
export function convertToCascaderOptions(data) {
  const result = [];
  const l0Map = new Map();
  const l1Map = new Map();
  const l2Map = new Map();

  data.forEach((item) => {
    const { reitsTypeL0, reitsTypeL1, reitsTypeL2, reitsTypeL3, reitsName, reitCode } = item;

    // Level 0
    if (!l0Map.has(reitsTypeL0)) {
      const l0Node = { label: reitsTypeL0, value: reitsTypeL0, children: [] };
      l0Map.set(reitsTypeL0, l0Node);
      result.push(l0Node);
    }
    const l0Node = l0Map.get(reitsTypeL0);

    // Level 1
    const l1Key = `${reitsTypeL0}/${reitsTypeL1}`;
    if (!l1Map.has(l1Key)) {
      const l1Node = { label: reitsTypeL1, value: reitsTypeL1, children: [] };
      l1Map.set(l1Key, l1Node);
      l0Node.children.push(l1Node);
    }
    const l1Node = l1Map.get(l1Key);

    // Level 2
    const l2Key = `${l1Key}/${reitsTypeL2}`;
    if (!l2Map.has(l2Key)) {
      const l2Node = { label: reitsTypeL2, value: reitsTypeL2, children: [] };
      l2Map.set(l2Key, l2Node);
      l1Node.children.push(l2Node);
    }
    const l2Node = l2Map.get(l2Key);

    // Level 3
    const l3Key = `${l2Key}/${reitsTypeL3}`;
    let l3Node = l2Node.children.find((c) => c.value === reitsTypeL3);
    if (!l3Node) {
      l3Node = { label: reitsTypeL3, value: reitsTypeL3, children: [] };
      l2Node.children.push(l3Node);
    }

    // Leaf node: REITs 产品
    l3Node.children.push({
      label: reitsName,
      value: reitCode,
    });
  });

  return [{ label: '公募REITs', value: '公募REITs' }, ...result];
}

// 使用示例
// import { convertToCascaderOptions } from '@/utils/cascader'
// const options = convertToCascaderOptions(data)
// <el-cascader :options="options" />

// 不带个股的Options，label/value 使用后端返回的 _label/_value 字段
export function buildCascaderOptions(data) {
  const tree = [];
  const map = {};

  data.forEach((item) => {
    const levels = [
      { label: item.reitsTypeL0_label, value: item.reitsTypeL0_value },
      { label: item.reitsTypeL1_label, value: item.reitsTypeL1_value },
      { label: item.reitsTypeL2_label, value: item.reitsTypeL2_value },
      { label: item.reitsTypeL3_label, value: item.reitsTypeL3_value },
    ];
    let currentLevel = tree;

    levels.forEach(({ label, value }) => {
      if (!value) return;

      if (!map[value]) {
        const node = { label, value, children: [] };
        map[value] = node;
        currentLevel.push(node);
      }
      currentLevel = map[value].children;
    });
  });

  // 移除空的 children 叶子节点
  const cleanTree = (nodes) =>
    nodes.map(({ children, ...rest }) => {
      if (children && children.length > 0) {
        return { ...rest, children: cleanTree(children) };
      }
      return rest;
    });

  return [{ label: '公募REITs', value: '公募REITs' }, ...cleanTree(tree)];
}

/**
 * 根据指定的层级字段生成 el-cascader 选项
 * @param {Array} data - 原始数据数组
 * @param {Array} levelFields - 层级字段名数组，如 ['reitsTypeL1', 'reitsTypeL2']
 * @param {Object} leafConfig - 叶子节点配置 { labelField: 'reitsName', valueField: 'reitCode' }
 * @returns {Array} el-cascader 选项数组
 */
export function buildCascaderOptionsByLevels(data, levelFields, leafConfig) {
  const root = [];
  const map = new Map();

  data.forEach((item) => {
    let pathKey = '';
    let currentLevel = root;

    // 逐层构建
    levelFields.forEach((field) => {
      const key = item[field];
      if (!key) return;
      pathKey = pathKey ? `${pathKey}/${key}` : key;

      if (!map.has(pathKey)) {
        const node = { label: key, value: key, children: [] };
        map.set(pathKey, node);
        currentLevel.push(node);
      }
      currentLevel = map.get(pathKey).children;
    });

    // 叶子节点
    const label = item[leafConfig.labelField];
    const value = item[leafConfig.valueField];
    if (!currentLevel.some((c) => c.value === value)) {
      currentLevel.push({ label, value });
    }
  });

  return root;
}
